import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Toast from '../../components/Common/Toast';
import ConfirmModal from '../../components/Common/ConfirmModal';
import UnitForm from '../../components/Configuration/UnitForm';
import { getUnits, deleteUnit } from '../../services/unitsService';

const Units = () => {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [unitToDelete, setUnitToDelete] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const hasLoaded = useRef(false);
  const [allUnits, setAllUnits] = useState([]); // Todas las unidades sin filtrar

  // Cargar unidades desde el backend (solo una vez)
  const loadUnits = async () => {
    try {
      setLoading(true);
      console.log('🔄 [Units] Iniciando carga de unidades...');
      
      const response = await getUnits(); // Sin parámetros, traer todas
      
      console.log('📥 [Units] Respuesta recibida en componente:');
      console.log('📦 [Units] response:', response);
      console.log('✅ [Units] response.success:', response.success);
      console.log('📋 [Units] response.data:', response.data);
      console.log('🔍 [Units] Tipo de response.data:', typeof response.data);
      console.log('📊 [Units] Es array?:', Array.isArray(response.data));
      
      if (response.success) {
        const unitsData = response.data || [];
        console.log('💾 [Units] Guardando unidades en estado:');
        console.log('🔢 [Units] Cantidad de unidades a guardar:', unitsData.length);
        console.log('📝 [Units] Primeras 3 unidades:', unitsData.slice(0, 3));
        
        if (unitsData.length > 0) {
          console.log('🔑 [Units] Estructura de la primera unidad:', {
            id: unitsData[0].id,
            code: unitsData[0].code,
            name: unitsData[0].name,
            symbol: unitsData[0].symbol,
            is_active: unitsData[0].is_active,
            description: unitsData[0].description,
            keys: Object.keys(unitsData[0])
          });
        }
        
        setAllUnits(unitsData); // Guardar todas las unidades
        console.log('✅ [Units] Unidades guardadas en estado exitosamente');
      } else {
        console.warn('⚠️ [Units] Respuesta sin éxito:', response);
        setToast({
          isOpen: true,
          message: 'Error al cargar las unidades de medida',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('❌ [Units] Error al cargar unidades:', error);
      console.error('📊 [Units] Error.response:', error.response);
      console.error('📋 [Units] Error.response?.data:', error.response?.data);
      console.error('🔢 [Units] Error.status:', error.response?.status);
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cargar las unidades de medida',
        type: 'error'
      });
    } finally {
      setLoading(false);
      console.log('🏁 [Units] Proceso de carga finalizado');
    }
  };

  useEffect(() => {
    // Evitar doble carga en React StrictMode - Carga inicial única
    if (!hasLoaded.current) {
      hasLoaded.current = true;
      loadUnits();
    }
  }, []);

  // Filtrar unidades localmente en el frontend
  const filteredUnits = allUnits.filter(unit => {
    const matchesSearch = !searchTerm || 
      unit.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      unit.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      unit.symbol.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && unit.is_active) ||
      (statusFilter === 'inactive' && !unit.is_active);

    return matchesSearch && matchesStatus;
  });

  // Calcular estadísticas basadas en las unidades filtradas
  const totalUnits = filteredUnits.length;
  const activeUnits = filteredUnits.filter(u => u.is_active).length;
  const inactiveUnits = filteredUnits.filter(u => !u.is_active).length;

  const handleDeleteClick = (unit) => {
    setUnitToDelete(unit);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!unitToDelete) return;
    
    try {
      const response = await deleteUnit(unitToDelete.id);
      
      if (response.success) {
        // Eliminar de allUnits
        setAllUnits(prevUnits => prevUnits.filter(u => u.id !== unitToDelete.id));
        setToast({
          isOpen: true,
          message: 'Unidad de medida eliminada exitosamente',
          type: 'success'
        });
      } else {
        setToast({
          isOpen: true,
          message: response.message || 'Error al eliminar la unidad de medida',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error al eliminar unidad:', error);
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al eliminar la unidad de medida',
        type: 'error'
      });
    } finally {
      setIsDeleteModalOpen(false);
      setUnitToDelete(null);
    }
  };

  const handleFormSuccess = (unitData) => {
    if (editingUnit) {
      // Actualizar la unidad en allUnits
      setAllUnits(prevUnits => 
        prevUnits.map(u => u.id === unitData.id ? unitData : u)
      );
      setToast({
        isOpen: true,
        message: `Unidad "${unitData.name}" actualizada exitosamente`,
        type: 'success'
      });
    } else {
      // Agregar nueva unidad a allUnits
      setAllUnits(prevUnits => [...prevUnits, unitData]);
      setToast({
        isOpen: true,
        message: `Unidad "${unitData.name}" creada exitosamente`,
        type: 'success'
      });
    }
    setIsFormOpen(false);
    setEditingUnit(null);
  };

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Unidades de Medida</h1>
              <p className="text-gray-600 mt-1">Gestione las unidades de medida disponibles para productos</p>
            </div>
            <button
              onClick={() => {
                setEditingUnit(null);
                setIsFormOpen(true);
              }}
              className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors"
            >
              + Nueva Unidad
            </button>
          </div>

          {/* Breadcrumb */}
          <nav className="text-sm text-gray-600 mb-4">
            <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
            <span className="mx-2">/</span>
            <Link to="/configuration" className="hover:text-teal-600">Configuración</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-800">Unidades de Medida</span>
          </nav>

          {/* Filtros y Búsqueda */}
          <div className="bg-white rounded-lg shadow-md p-4 mb-4">
            <div className="flex gap-2">
              {/* Filtro por Estado */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="all">Todos los Estados</option>
                <option value="active">Activos</option>
                <option value="inactive">Inactivos</option>
              </select>

              {/* Búsqueda */}
              <input
                type="text"
                placeholder="Buscar por código, nombre o símbolo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              <button
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                }}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
              >
                Limpiar Filtros
              </button>
            </div>
          </div>
        </div>

        {/* Estadísticas - ENCIMA DE LA TABLA */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-gray-600">Total de Unidades</div>
            <div className="text-2xl font-bold text-gray-800">{totalUnits}</div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-gray-600">Unidades Activas</div>
            <div className="text-2xl font-bold text-green-600">{activeUnits}</div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-gray-600">Unidades Inactivas</div>
            <div className="text-2xl font-bold text-red-600">{inactiveUnits}</div>
          </div>
        </div>

        {/* Tabla */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">
              Cargando unidades de medida...
            </div>
          ) : filteredUnits.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {searchTerm || statusFilter !== 'all' 
                ? 'No se encontraron unidades con los filtros aplicados' 
                : 'No hay unidades de medida registradas'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Código
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nombre
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Símbolo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUnits.map((unit) => (
                    <tr key={unit.id} className="hover:bg-gray-50">
                      {/* Código */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-mono font-medium text-teal-600">{unit.code}</span>
                      </td>

                      {/* Nombre */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900">{unit.name}</span>
                      </td>

                      {/* Símbolo */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">{unit.symbol}</span>
                      </td>

                      {/* Estado */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          unit.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {unit.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>

                      {/* Acciones */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => {
                            setEditingUnit(unit);
                            setIsFormOpen(true);
                          }}
                          className="text-purple-600 hover:text-purple-900 mr-4"
                          title="Editar"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDeleteClick(unit)}
                          className="text-red-600 hover:text-red-900"
                          title="Eliminar"
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modal de Formulario de Crear/Editar */}
        <UnitForm
          isOpen={isFormOpen}
          onClose={() => {
            setIsFormOpen(false);
            setEditingUnit(null);
          }}
          unit={editingUnit}
          onSuccess={handleFormSuccess}
        />

        {/* Modal de Confirmación de Eliminación */}
        <ConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setUnitToDelete(null);
          }}
          onConfirm={handleDeleteConfirm}
          title="Eliminar Unidad de Medida"
          message={`¿Estás seguro de que deseas eliminar la unidad "${unitToDelete?.name}" (${unitToDelete?.code})? Esta acción no se puede deshacer.`}
          confirmText="Eliminar"
          cancelText="Cancelar"
          type="danger"
        />

        {/* Toast de notificaciones */}
        <Toast
          isOpen={toast.isOpen}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, isOpen: false })}
        />
      </div>
    </div>
  );
};

export default Units;

